import React from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { cn } from '../../../utils/cn';

const ContactTable = ({
  contacts,
  onEdit,
  onDelete,
  onSort,
  sortConfig,
  selectedContacts,
  onToggleSelect,
  onToggleSelectAll,
  onScoreContact,
  scoringInProgress
}) => {
  const isUpcoming = (date) => {
    const appointmentDate = new Date(date);
    const today = new Date();
    const diffTime = appointmentDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays >= 0 && diffDays <= 7;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getSortIcon = (key) => {
    if (sortConfig?.key !== key) {
      return <Icon name="ArrowUpDown" size={14} className="text-muted-foreground" />;
    }
    return sortConfig?.direction === 'asc'
      ? <Icon name="ArrowUp" size={14} className="text-primary" />
      : <Icon name="ArrowDown" size={14} className="text-primary" />;
  };

  const getScoreBadgeColor = (score) => {
    if (score >= 75) return 'bg-green-100 text-green-800 border-green-200';
    if (score >= 50) return 'bg-blue-100 text-blue-800 border-blue-200';
    if (score >= 25) return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    return 'bg-red-100 text-red-800 border-red-200';
  };

  const allSelected = contacts?.length > 0 && selectedContacts?.length === contacts?.length;
  const someSelected = selectedContacts?.length > 0 && selectedContacts?.length < contacts?.length;

  return (
    <>
      {/* Desktop Table View */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/50 border-b border-border">
            <tr>
              <th className="px-4 py-3 text-left">
                <input
                  type="checkbox"
                  checked={allSelected}
                  ref={input => {
                    if (input) {
                      input.indeterminate = someSelected;
                    }
                  }}
                  onChange={onToggleSelectAll}
                  className="h-4 w-4 rounded border-input bg-background text-primary focus:ring-2 focus:ring-ring"
                />
              </th>
              <th className="px-4 py-3 text-left">
                <button
                  onClick={() => onSort('name')}
                  className="flex items-center gap-2 text-sm font-semibold text-foreground hover:text-primary transition-colors"
                >
                  Name
                  {getSortIcon('name')}
                </button>
              </th>
              <th className="px-4 py-3 text-left">
                <button
                  onClick={() => onSort('email')}
                  className="flex items-center gap-2 text-sm font-semibold text-foreground hover:text-primary transition-colors"
                >
                  Email
                  {getSortIcon('email')}
                </button>
              </th>
              <th className="px-4 py-3 text-left">
                <button
                  onClick={() => onSort('companyName')}
                  className="flex items-center gap-2 text-sm font-semibold text-foreground hover:text-primary transition-colors"
                >
                  Company Name
                  {getSortIcon('companyName')}
                </button>
              </th>
              <th className="px-4 py-3 text-left">
                <button
                  onClick={() => onSort('appointmentDate')}
                  className="flex items-center gap-2 text-sm font-semibold text-foreground hover:text-primary transition-colors"
                >
                  Appointment Date
                  {getSortIcon('appointmentDate')}
                </button>
              </th>
              <th className="px-4 py-3 text-left">
                <button
                  onClick={() => onSort('leadScore')}
                  className="flex items-center gap-2 text-sm font-semibold text-foreground hover:text-primary transition-colors"
                >
                  Lead Score
                  {getSortIcon('leadScore')}
                </button>
              </th>
              <th className="px-4 py-3 text-right">
                <span className="text-sm font-semibold text-foreground">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {contacts?.map((contact) => (
              <tr
                key={contact?.id}
                className={cn(
                  "hover:bg-muted/30 transition-colors",
                  selectedContacts?.includes(contact?.id) && "bg-muted/50"
                )}
              >
                <td className="px-4 py-4">
                  <input
                    type="checkbox"
                    checked={selectedContacts?.includes(contact?.id)}
                    onChange={() => onToggleSelect(contact?.id)}
                    className="h-4 w-4 rounded border-input bg-background text-primary focus:ring-2 focus:ring-ring"
                  />
                </td>
                <td className="px-4 py-4">
                  <div className="flex items-center gap-2">
                    <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                      <span className="text-sm font-semibold text-primary">
                        {contact?.name?.charAt(0)?.toUpperCase()}
                      </span>
                    </div>
                    <span className="text-sm font-medium text-foreground">{contact?.name}</span>
                  </div>
                </td>
                <td className="px-4 py-4">
                  <a
                    href={`mailto:${contact?.email}`}
                    className="text-sm text-primary hover:underline"
                  >
                    {contact?.email}
                  </a>
                </td>
                <td className="px-4 py-4">
                  <span className="text-sm text-foreground">{contact?.companyName}</span>
                </td>
                <td className="px-4 py-4">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-foreground">{formatDate(contact?.appointmentDate)}</span>
                    {isUpcoming(contact?.appointmentDate) && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-warning/10 text-warning border border-warning/20">
                        <Icon name="Clock" size={12} className="mr-1" />
                        Upcoming
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-4 py-4">
                  {contact?.leadScore !== null && contact?.leadScore !== undefined ? (
                    <div className="flex items-center gap-2">
                      <span className={cn(
                        "inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold border",
                        getScoreBadgeColor(contact?.leadScore)
                      )}>
                        <Icon name="Sparkles" size={12} className="mr-1" />
                        {contact?.leadScore}
                      </span>
                      {contact?.leadScoreReasoning && (
                        <button
                          onClick={() => alert(contact?.leadScoreReasoning)}
                          className="text-muted-foreground hover:text-foreground transition-colors"
                          title="View reasoning"
                        >
                          <Icon name="Info" size={14} />
                        </button>
                      )}
                    </div>
                  ) : (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onScoreContact(contact?.id)}
                      iconName="Sparkles"
                      iconPosition="left"
                      disabled={scoringInProgress?.has(contact?.id)}
                    >
                      {scoringInProgress?.has(contact?.id) ? 'Scoring...' : 'Score'}
                    </Button>
                  )}
                </td>
                <td className="px-4 py-4">
                  <div className="flex items-center justify-end gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onEdit(contact)}
                      iconName="Edit"
                      iconPosition="left"
                    >
                      Edit
                    </Button>
                    {contact?.leadScore !== null && contact?.leadScore !== undefined && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onScoreContact(contact?.id)}
                        iconName="RefreshCw"
                        disabled={scoringInProgress?.has(contact?.id)}
                        title="Re-score contact"
                      />
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onDelete(contact?.id)}
                      iconName="Trash2"
                      className="text-destructive hover:text-destructive hover:bg-destructive/10"
                    >
                      Delete
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile Card View */}
      <div className="md:hidden divide-y divide-border">
        {contacts?.map((contact) => (
          <div
            key={contact?.id}
            className={cn(
              "p-4",
              selectedContacts?.includes(contact?.id) && "bg-muted/50"
            )}
          >
            <div className="flex items-start gap-3 mb-3">
              <input
                type="checkbox"
                checked={selectedContacts?.includes(contact?.id)}
                onChange={() => onToggleSelect(contact?.id)}
                className="h-4 w-4 mt-1 rounded border-input bg-background text-primary focus:ring-2 focus:ring-ring"
              />
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <span className="text-base font-semibold text-primary">
                      {contact?.name?.charAt(0)?.toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <h3 className="text-base font-semibold text-foreground">{contact?.name}</h3>
                    <p className="text-sm text-muted-foreground">{contact?.companyName}</p>
                  </div>
                </div>
                <div className="space-y-2 mb-3">
                  <div className="flex items-center gap-2">
                    <Icon name="Mail" size={14} className="text-muted-foreground" />
                    <a
                      href={`mailto:${contact?.email}`}
                      className="text-sm text-primary hover:underline"
                    >
                      {contact?.email}
                    </a>
                  </div>
                  <div className="flex items-center gap-2">
                    <Icon name="Calendar" size={14} className="text-muted-foreground" />
                    <span className="text-sm text-foreground">{formatDate(contact?.appointmentDate)}</span>
                    {isUpcoming(contact?.appointmentDate) && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-warning/10 text-warning border border-warning/20">
                        <Icon name="Clock" size={10} className="mr-1" />
                        Upcoming
                      </span>
                    )}
                  </div>
                  {contact?.leadScore !== null && contact?.leadScore !== undefined && (
                    <div className="flex items-center gap-2">
                      <Icon name="Sparkles" size={14} className="text-muted-foreground" />
                      <span className={cn(
                        "inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold border",
                        getScoreBadgeColor(contact?.leadScore)
                      )}>
                        Score: {contact?.leadScore}
                      </span>
                      {contact?.leadScoreReasoning && (
                        <button
                          onClick={() => alert(contact?.leadScoreReasoning)}
                          className="text-muted-foreground hover:text-foreground transition-colors"
                          title="View reasoning"
                        >
                          <Icon name="Info" size={12} />
                        </button>
                      )}
                    </div>
                  )}
                </div>
                <div className="flex gap-2">
                  {contact?.leadScore === null || contact?.leadScore === undefined ? (
                    <Button
                      variant="default"
                      size="sm"
                      onClick={() => onScoreContact(contact?.id)}
                      iconName="Sparkles"
                      iconPosition="left"
                      disabled={scoringInProgress?.has(contact?.id)}
                      fullWidth
                    >
                      {scoringInProgress?.has(contact?.id) ? 'Scoring...' : 'Score Lead'}
                    </Button>
                  ) : (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onScoreContact(contact?.id)}
                      iconName="RefreshCw"
                      iconPosition="left"
                      disabled={scoringInProgress?.has(contact?.id)}
                      fullWidth
                    >
                      {scoringInProgress?.has(contact?.id) ? 'Scoring...' : 'Re-score'}
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onDelete(contact?.id)}
                    iconName="Trash2"
                    className="text-destructive border-destructive hover:bg-destructive/10"
                    fullWidth
                  >
                    Delete
                  </Button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </>
  );
};

export default ContactTable;